import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Initiate an AI-powered outbound call to a lead
 * POST /api/ai-dialer/call
 *
 * @param {Object} params
 * @param {string} params.lead_id - The lead ID to call
 * @param {string} [params.custom_context] - Additional context for the AI
 * @param {string[]} [params.preferred_callback_slots] - Preferred callback times
 * @returns {Promise<{call_id: string, status: string, message: string}>}
 */
export async function initiateAICall({ lead_id, custom_context, preferred_callback_slots }) {
    const response = await fetchWithAuth(`${BASE_URL}/api/ai-dialer/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            lead_id,
            custom_context: custom_context || null,
            preferred_callback_slots: preferred_callback_slots || []
        })
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = await response.json();
            const err = new Error(error.detail?.message || 'Insufficient credits for AI call');
            err.code = 'INSUFFICIENT_CREDITS';
            err.credits_required = error.detail?.credits_required;
            err.credits_available = error.detail?.credits_available;
            throw err;
        }
        if (response.status === 403) {
            throw new Error('AI Dialer requires Ultra subscription');
        }
        logError('Failed to initiate AI call', response.status);
        throw new Error('Failed to initiate AI call');
    }

    return response.json();
}

/**
 * Get status of an AI call
 * GET /api/ai-dialer/calls/{call_id}
 *
 * @param {string} callId - The AI call ID
 * @returns {Promise<Object>} AI call details with status and result
 */
export async function getAICallStatus(callId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/ai-dialer/calls/${callId}`);

    if (!response.ok) {
        if (response.status === 404) {
            return null;
        }
        logError(`Failed to get AI call status for ${callId}`, response.status);
        throw new Error('Failed to get AI call status');
    }

    return response.json();
}

/**
 * List AI calls with optional filters
 * GET /api/ai-dialer/calls
 *
 * @param {Object} [options]
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Results per page
 * @param {string} [options.status] - Filter by status
 * @param {string} [options.lead_id] - Filter by lead ID
 * @returns {Promise<{calls: Array, total: number, page: number, limit: number}>}
 */
export async function listAICalls({ page = 1, limit = 20, status, lead_id } = {}) {
    const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
    });
    if (status) params.append('status', status);
    if (lead_id) params.append('lead_id', lead_id);

    const response = await fetchWithAuth(`${BASE_URL}/api/ai-dialer/calls?${params}`);

    if (!response.ok) {
        logError('Failed to list AI calls', response.status);
        throw new Error('Failed to list AI calls');
    }

    return response.json();
}

/**
 * Get AI Dialer statistics
 * GET /api/ai-dialer/stats
 *
 * @returns {Promise<Object>} Stats including total calls, interested leads, conversion rate
 */
export async function getAIDialerStats() {
    const response = await fetchWithAuth(`${BASE_URL}/api/ai-dialer/stats`);

    if (!response.ok) {
        if (response.status === 404) {
            return {
                total_calls: 0,
                calls_today: 0,
                successful_calls: 0,
                interested_leads: 0,
                callbacks_scheduled: 0,
                average_duration_seconds: 0,
                conversion_rate: 0
            };
        }
        logError('Failed to get AI dialer stats', response.status);
        throw new Error('Failed to get AI dialer stats');
    }

    return response.json();
}

/**
 * Get AI Dialer configuration for the user
 * GET /api/ai-dialer/config
 *
 * @returns {Promise<Object>} Config including limits, credits per call, calling hours
 */
export async function getAIDialerConfig() {
    const response = await fetchWithAuth(`${BASE_URL}/api/ai-dialer/config`);

    if (!response.ok) {
        if (response.status === 404) {
            return {
                enabled: false,
                credits_per_call: 250,
                daily_call_limit: 50,
                calls_remaining_today: 0
            };
        }
        logError('Failed to get AI dialer config', response.status);
        throw new Error('Failed to get AI dialer config');
    }

    return response.json();
}
